import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import type { ContactInvestmentInfo } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactInvestmentInfo;
}

const INVESTMENT_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'accreditedInvestor', formField: 'accreditedInvestor' },
  { apiField: 'qualifiedPurchaser', formField: 'qualifiedPurchaser' },
  { apiField: 'currentInvestor', formField: 'currentInvestor' },
  {
    apiField: 'estimatedProbabilityClosing',
    formField: 'estimatedProbabilityOfClosing'
  },
  { apiField: 'likelyInvestmentAmount', formField: 'likelyInvestmentAmount' },
  { apiField: 'bdc', formField: 'bdc' },
  {
    apiField: 'starMountainAnnualShareholderMeeting',
    formField: 'starMountainAnnualShareholderMeeting'
  }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactInvestmentInfo,
  initial: ContactInvestmentInfo
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      if (typeof currentVal === 'boolean') {
        changes[apiField] = currentVal;
      } else {
        changes[apiField] = currentVal === '' ? null : currentVal;
      }
    }
  }

  return changes;
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        accreditedInvestor
        qualifiedPurchaser
        currentInvestor
        estimatedProbabilityClosing
        likelyInvestmentAmount
        bdc
        starMountainAnnualShareholderMeeting
      }
    }
  }
`;

export interface EditInvestmentInfoDialogProps {
  contactId: string;
  initialValue: ContactInvestmentInfo;
  isOpen: boolean;
  onChange: (data: ContactInvestmentInfo) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactInvestmentInfo;
}

export function EditInvestmentInfoDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditInvestmentInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(INVESTMENT_INFO_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactInvestmentInfo>(
    field: K,
    fieldValue: ContactInvestmentInfo[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save investment information');
      } else {
        toast.success('Investment information saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving investment information:', error);
      toast.error('Failed to save investment information');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Investment Information"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditCheckboxField
          checked={value.accreditedInvestor}
          label="Accredited Investor"
          onCheckedChange={accreditedInvestor => {
            updateField('accreditedInvestor', accreditedInvestor);
          }}
        />
        <EditCheckboxField
          checked={value.qualifiedPurchaser}
          label="Qualified Purchaser"
          onCheckedChange={qualifiedPurchaser => {
            updateField('qualifiedPurchaser', qualifiedPurchaser);
          }}
        />
        <EditCheckboxField
          checked={value.currentInvestor}
          label="Current Investor"
          onCheckedChange={currentInvestor => {
            updateField('currentInvestor', currentInvestor);
          }}
        />
        <EditFormField
          label="Estimated Probability of Closing"
          onChange={v => {
            updateField('estimatedProbabilityOfClosing', v);
          }}
          value={value.estimatedProbabilityOfClosing}
        />
        <EditDynamicSelectField
          fieldName="likely_investment_amount"
          label="Likely Investment Amount"
          model="res.partner"
          onChange={v => {
            updateField('likelyInvestmentAmount', v);
          }}
          value={value.likelyInvestmentAmount}
        />
        <EditCheckboxField
          checked={value.bdc}
          label="BDC"
          onCheckedChange={bdc => {
            updateField('bdc', bdc);
          }}
        />
        <EditCheckboxField
          checked={value.starMountainAnnualShareholderMeeting}
          label="Star Mountain Annual Shareholder Meeting"
          onCheckedChange={starMountainAnnualShareholderMeeting => {
            updateField(
              'starMountainAnnualShareholderMeeting',
              starMountainAnnualShareholderMeeting
            );
          }}
        />
      </div>
    </EditDialog>
  );
}
